import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Download, ChevronDown } from "lucide-react";

const S = (title, img, caption, points) => ({ title, img, caption, points });

const CONTENT = {
  coach: {
    label: "Guía del Entrenador y Delegado",
    intro: "Todo tu trabajo semanal en una pantalla de móvil: jornadas, pizarra, convocatorias, asistencia y evaluaciones. Guárdate esta página — está siempre aquí, en tu menú.",
    sections: [
      S("1 · Tu primer día", "coach-equipos", "Tu pantalla de inicio: Mis Equipos", [
        "Entra con el email y la contraseña que te llegaron por correo (puedes cambiarla desde tu perfil).",
        "En «Mis Equipos» ves los equipos que te ha asignado tu coordinador — solo ves los tuyos.",
        "Truco: abre la web en el móvil y usa «Añadir a pantalla de inicio» — funciona como una app normal, sin descargar nada.",
      ]),
      S("2 · Registrar un partido en 3 minutos", "coach-jornadas", "Jornadas: tu acta digital (Borrador → Final)", [
        "Menú «Jornadas» → botón «Nueva Jornada» → tipo Partido → rival, fecha, casa/fuera.",
        "Marca los convocados y rellena minutos, goles (con su minuto), asistencias, tarjetas y cambios.",
        "Guarda como BORRADOR si quieres terminarlo con calma: puedes editarlo las veces que necesites.",
        "Cuando esté completo, pulsa FINALIZAR: entonces (y solo entonces) cuenta para las estadísticas.",
      ]),
      S("2b · El formulario de la jornada", "coach-jornada-nueva", "Nueva jornada: funciona como un acta arbitral", [
        "Todo está pensado para rellenarlo desde el banquillo o al llegar a casa.",
        "Los jugadores salen de tu plantilla — si falta alguno, se añade en «Jugadores».",
      ]),
      S("3 · Pizarra táctil (partido en directo)", "coach-pizarra", "La pizarra: fichas con foto, cambios y acciones en vivo", [
        "Cada jugador aparece con su FOTO real, su dorsal y su nombre — si no tiene foto, se ve el dorsal grande. Sube las fotos en «Jugadores».",
        "Insignias en vivo sobre cada ficha: ⚽ si ha marcado (×2, ×3…), 🟨 si tiene amarilla y ▲ verde si entró de cambio. El partido se lee de un vistazo.",
        "Carga SOLO los jugadores convocados en el evento del calendario — convoca antes del partido.",
        "CAMBIO: toca al reserva del banquillo y después al jugador del campo que sale. En el registro se ve en colores: Sale (rojo) → Entra (verde).",
        "TARJETA ROJA (botón rojo): el jugador sale del campo SIN sustituto, como en el fútbol real.",
        "Al pitar el final, guarda: minutos, goles y tarjetas pasan solos a la jornada y a las estadísticas.",
      ]),
      S("3b · Leer el partido de un vistazo", "coach-pizarra-eventos", "Banquillo con fotos y registro de eventos a color", [
        "El banquillo muestra a los reservas con su foto en rojo — toca uno y luego al que sale para hacer el cambio.",
        "Cada evento queda en la lista con su minuto, icono y color: ⚽ gol (verde), 🟨 amarilla (ámbar), 🔁 cambio (sale rojo / entra verde).",
        "¿Te equivocaste? Borra el evento con la ✕ y todo se recalcula.",
      ]),
      S("3c · 📤 Compartir la alineación por WhatsApp", "alineacion-ejemplo", "Una imagen profesional lista para el grupo de las familias", [
        "En la pizarra, pulsa «📤 Compartir alineación (imagen)» (debajo de los botones Pizarra/Datos).",
        "Se genera una imagen con el campo, las fotos y dorsales, el marcador y el banquillo — en el móvil se abre directo el menú de compartir (WhatsApp).",
        "Hazla antes del partido (alineación inicial) o después: incluye los goles ⚽ y tarjetas 🟨 de cada jugador.",
      ]),
      S("4 · Calendario y convocatorias a las familias", "coach-calendario", "El calendario del club con convocatorias por email", [
        "Crea el partido o entreno en «Calendario» y marca los convocados.",
        "A cada familia le llega un email con los detalles y botones para confirmar asistencia (RSVP).",
        "El día antes del partido, a las 9:00, te llega a ti un email con la convocatoria numerada.",
        "Los emails de los padres salen de la ficha del jugador — revisa que estén rellenos.",
      ]),
      S("5 · Pasar asistencia (30 segundos)", "coach-asistencia", "Asistencia: toca presente/ausente y listo", [
        "Menú «Asistencia» → elige el día → toca el estado de cada jugador.",
        "El sistema calcula el ranking mensual de asistencia por equipo solo.",
      ]),
      S("6 · Evaluación individual (escala 1-5)", "coach-evaluaciones", "Evaluaciones con gráfico radar y PDF para familias", [
        "Puntúa técnica, físico y actitud según la plantilla que defina tu coordinador.",
        "Recomendado: una evaluación al final de cada mes o tras cada 3-4 jornadas.",
        "Desde la ficha del jugador puedes generar el PDF con el gráfico radar para enviar a la familia.",
      ]),
      S("7 · Lesiones", "coach-lesiones", "Registro de lesiones con línea de tiempo", [
        "Registra la lesión (zona, fecha, estado) y ve actualizando hasta el alta.",
        "El jugador lesionado queda marcado en plantilla y convocatorias.",
      ]),
    ],
  },
  coordinator: {
    label: "Guía del Coordinador y Director Deportivo",
    intro: "Tu papel: que todos los equipos trabajen igual y que los datos del club estén vivos. Desde aquí supervisas todo sin perseguir a nadie por WhatsApp.",
    sections: [
      S("1 · Vista Global del club", "coord-global", "Vista Global: todo el club de un vistazo", [
        "Categorías, equipos, jugadores y actividad reciente en una sola pantalla.",
        "Es tu página de inicio cada mañana: detecta equipos parados de un vistazo.",
      ]),
      S("2 · Plantillas de Análisis (lo más importante)", "coord-plantillas", "Define QUÉ se evalúa en cada categoría", [
        "En «Plantillas de Análisis» defines los conceptos que puntúan los entrenadores (escala 1-5) por categoría.",
        "Puedes crear indicadores custom también para las estadísticas de equipo.",
        "Hazlo ANTES de que los entrenadores empiecen a evaluar: así todo el club mide lo mismo.",
      ]),
      S("3 · Estadísticas del club", "coord-stats", "Estadísticas globales con filtros y exportación", [
        "Filtra por categoría, equipo o jugador. Exporta a Excel o PDF.",
        "«Informe PDF por equipo» te llega directo a tu email con un click.",
      ]),
      S("4 · Equipos y staff", null, null, [
        "Asigna entrenadores y delegados a cada equipo desde «Equipos» — cada uno solo ve lo suyo.",
        "El alta de nuevos entrenadores la hace el admin del club en «Equipo Técnico» (les llega email con credenciales).",
      ]),
      S("5 · Supervisión del día a día", null, null, [
        "Jornadas: ves los borradores (actas) de todos los equipos y cuáles faltan por finalizar.",
        "Evaluaciones: controla qué entrenadores evalúan y cuáles no.",
        "Asistencia mensual y Lesiones: visión de todo el club.",
      ]),
      S("6 · Comunicación y calendario", null, null, [
        "«Comunicación»: avisos por ámbito (todo el club, una categoría o un equipo).",
        "Calendario: puedes copiar una semana entera de eventos con fecha límite y saltándote festivos.",
      ]),
      S("7 · 🔴 Partidos en directo", "coord-endirecto", "Sigue los partidos de tus entrenadores en vivo", [
        "Cuando un entrenador arranca la Pizarra en un partido, en tu panel aparece la sección «🔴 En directo» con una tarjeta por partido: equipo, rival, marcador y minuto.",
        "Toca la tarjeta y entras en modo espectador: ves el campo con los jugadores, el marcador, el crono y cada evento (goles, tarjetas, cambios) actualizándose solo cada pocos segundos.",
        "Es solo lectura: tú miras, el entrenador (o delegado) registra. Perfecto para seguir varios campos a la vez un sábado por la mañana.",
      ]),
    ],
  },
  club_admin: {
    label: "Guía del Club (Administrador)",
    intro: "Eres quien lo ve todo: configuración, equipo técnico y facturación. La puesta en marcha completa son 15 minutos.",
    sections: [
      S("1 · Puesta en marcha en 15 minutos", "admin-panel", "Tu panel: el estado del club de un vistazo", [
        "Paso 1 — Sube el escudo en «Datos del Club» (2 min).",
        "Paso 2 — Revisa/crea las categorías (U8, U10, U12…) con su plantilla de puntuación.",
        "Paso 3 — Crea los equipos dentro de cada categoría.",
        "Paso 4 — Da de alta el staff en «Equipo Técnico» (les llega email con su acceso).",
        "Paso 5 — Los entrenadores (o tú) añadís los jugadores — también por importación desde Excel/CSV.",
      ]),
      S("2 · Equipos y jugadores", "admin-equipos", "Equipos organizados por categoría", [
        "Cada equipo tiene su plantilla, su staff asignado y sus estadísticas.",
        "En «Jugadores» está la base de datos completa del club, con ficha individual y foto.",
      ]),
      S("3 · Equipo Técnico (invitar staff)", "admin-staff", "Alta de coordinadores, entrenadores y delegados", [
        "«Equipo Técnico» → Añadir → elige el rol: coordinador ve todo el club; entrenador/delegado solo sus equipos.",
        "Al crearlos les llega un email automático con sus credenciales.",
        "Si alguien no puede entrar: desde su tarjeta puedes restablecer su contraseña.",
      ]),
      S("4 · El día a día (igual que tu staff)", null, null, [
        "Tienes acceso a todo lo del coordinador y entrenador: jornadas, pizarra, calendario, asistencia, evaluaciones, lesiones y estadísticas.",
        "Consejo: deja el trabajo diario al staff y usa tú las «Estadísticas» y los informes.",
      ]),
      S("4b · 🔴 Partidos en directo", "live-espectador", "Mira los partidos de tus equipos en vivo", [
        "Cuando un entrenador usa la Pizarra en un partido, en tu panel aparece «🔴 En directo»: tocas la tarjeta y ves el campo, el marcador y los eventos en vivo, sin poder tocar nada.",
        "Se actualiza solo cada pocos segundos — ideal para seguir al club desde casa o desde otro campo.",
      ]),
      S("5 · Facturación", "admin-billing", "Plan, pagos y facturas en autoservicio", [
        "En «Facturación» eliges plan, pagas con tarjeta o SEPA y descargas las facturas.",
        "Prueba gratuita de 15 días sin tarjeta. Pago anual = 12 meses al precio de 11.",
        "Tu precio queda garantizado 24 meses. Sin permanencia.",
      ]),
      S("6 · Soporte", null, null, [
        "Responde a cualquier email que te mandemos o escribe a sudefootball@sudeporte.com · +34 624 037 426.",
        "Las guías de tu staff (coordinador y entrenador) las tienen ellos en su propio menú «Guía de uso».",
      ]),
    ],
  },
};
CONTENT.director_deportivo = CONTENT.coordinator;
CONTENT.delegado = CONTENT.coach;

function Section({ s, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border border-border" data-testid={`guide-section-${idx}`}>
      <button onClick={() => setOpen(o => !o)} data-testid={`guide-section-toggle-${idx}`}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-secondary/40 transition-colors">
        <span className="font-heading font-black uppercase tracking-tight text-sm md:text-base">{s.title}</span>
        <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-5 pt-1 space-y-4">
          <ul className="space-y-2">
            {s.points.map((p, i) => (
              <li key={i} className="text-sm leading-relaxed flex gap-2">
                <span className="text-primary font-black shrink-0">›</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
          {s.img && (
            <figure className="border border-border bg-black/20">
              <img src={`/guide/${s.img}.png`} alt={s.caption || s.title} loading="lazy" className="w-full" />
              {s.caption && <figcaption className="text-[11px] text-muted-foreground px-3 py-2 border-t border-border">📸 {s.caption}</figcaption>}
            </figure>
          )}
        </div>
      )}
    </div>
  );
}

export default function GuidePage() {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const data = CONTENT[user?.role];
  if (!data) return <div className="p-6 text-sm text-muted-foreground">No hay guía disponible para tu rol.</div>;

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/guide/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Guia-Sudefootball.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="guide-page">
      <div className="overline text-primary flex items-center gap-2"><BookOpen size={14} /> Guía de uso</div>
      <h1 className="font-heading font-black uppercase text-2xl md:text-3xl tracking-tight mt-1">{data.label}</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-xl">{data.intro}</p>
      <button onClick={downloadPdf} disabled={downloading} data-testid="guide-download-pdf-btn"
        className="mt-4 h-10 px-5 inline-flex items-center gap-2 border-2 border-primary text-primary text-xs uppercase font-heading font-black tracking-widest hover:bg-primary/10 disabled:opacity-50">
        <Download size={14} /> {downloading ? "Descargando…" : "Descargar guía en PDF"}
      </button>
      <div className="mt-6 space-y-3">
        {data.sections.map((s, i) => <Section key={i} s={s} idx={i} />)}
      </div>
      <div className="mt-8 border border-border p-4 text-sm">
        ¿Atascado en algo? Escríbenos a <b>sudefootball@sudeporte.com</b> o al <b>+34 624 037 426</b> y lo vemos juntos en 5 minutos. ⚽
      </div>
    </div>
  );
}
